import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { api, type AppointmentsListParams } from '@/lib/api-client'

/** Paginated/filtered appointments list (rows or patients view). */
export function useAppointments(params?: AppointmentsListParams) {
  return useQuery({
    queryKey: ['appointments', params],
    queryFn: () => api.appointments.list(params),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  })
}

/** Single appointment with patient + invoice joins. */
export function useAppointment(id: number | null) {
  return useQuery({
    queryKey: ['appointment', id],
    queryFn: () => api.appointments.get(id!),
    enabled: id !== null && id > 0,
    staleTime: 60 * 1000,
  })
}

/** Distinct creators + doctors per branch — populates filter dropdowns. */
export function useAppointmentFilters(branchId?: number) {
  return useQuery({
    queryKey: ['appointment-filters', branchId],
    queryFn: () => api.appointments.filters(branchId),
    staleTime: 5 * 60 * 1000, // 5 min — slow-moving
  })
}

/** All appointments for a single patient across all dates. */
export function useAppointmentsByPatient(args: { pno?: string; patientId?: number; branchId?: number }) {
  return useQuery({
    queryKey: ['appointments-by-patient', args],
    queryFn: () => api.appointments.byPatient(args),
    enabled: !!(args.pno || args.patientId),
    staleTime: 30 * 1000,
  })
}
